// src/components/ProfileSidebar.js

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { DEV_MODE } from "../config";
import "./ProfileSidebar.css";

const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN?.trim();
const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID?.trim();

const EMAIL_FREQUENCY_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "off", label: "Off" },
];

const ProfileSidebar = ({
  isOpen,
  userId,
  userEmail,
  userName,
  emailFrequency,
  onEmailFrequencyChange,
}) => {
  const [userInfo, setUserInfo] = useState(null);
  const [editName, setEditName] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [passwordStatus, setPasswordStatus] = useState("");
  const [sendingReset, setSendingReset] = useState(false);
  const [localDigestFrequency, setLocalDigestFrequency] = useState("daily");
  const [savingFrequency, setSavingFrequency] = useState(false);
  const [frequencyStatus, setFrequencyStatus] = useState("");
  const [frequencyStatusMessage, setFrequencyStatusMessage] = useState("");

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        if (DEV_MODE) {
          setUserInfo({
            _id: "dev_user_id",
            user_metadata: { full_name: "Dev User" },
            email: "devuser@example.com",
          });
        } else if (userId) {
          const response = await axios.get(
            `/.netlify/functions/getUserData?userId=${userId}`
          );
          setUserInfo(response.data);
        }
      } catch (error) {
        console.error("Error fetching user information:", error);
      }
    };
    if (isOpen) {
      fetchUserInfo();
      setSaveMessage("");
      setPasswordStatus("");
      setIsEditingName(false);
    }
  }, [isOpen, userId]);

  useEffect(() => {
    if (userInfo) {
      setEditName(
        userInfo?.user_metadata?.full_name ||
          userInfo?.name ||
          userName ||
          ""
      );
    }
  }, [userInfo, userName]);

  // Standalone fallback: when the parent does not own the frequency state,
  // hydrate it from the fetched user data.
  useEffect(() => {
    if (userInfo) {
      setLocalDigestFrequency(
        userInfo.emailFrequency ||
          (userInfo.newsletterEnabled === false ? "off" : "daily")
      );
    }
  }, [userInfo]);

  const handleSaveName = useCallback(async () => {
    if (!editName.trim() || savingName) return;
    setSavingName(true);
    setSaveMessage("");
    try {
      await axios.post("/.netlify/functions/saveUserData", {
        userId,
        user_metadata: { full_name: editName.trim() },
      });
      setSaveMessage("Name saved");
      setIsEditingName(false);
    } catch (error) {
      console.error("Error saving name:", error);
      setSaveMessage("Failed to save");
    } finally {
      setSavingName(false);
    }
  }, [editName, savingName, userId]);

  const handleChangePassword = useCallback(async () => {
    const email = userInfo?.email || userEmail;
    if (!email || !auth0Domain || !auth0ClientId) {
      setPasswordStatus("error");
      return;
    }
    setSendingReset(true);
    setPasswordStatus("");
    try {
      await fetch(`https://${auth0Domain}/dbconnections/change_password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client_id: auth0ClientId,
          email,
          connection: "Username-Password-Authentication",
        }),
      });
      setPasswordStatus("success");
    } catch (error) {
      console.error("Error requesting password reset:", error);
      setPasswordStatus("error");
    } finally {
      setSendingReset(false);
    }
  }, [userInfo?.email, userEmail]);

  const digestFrequency = emailFrequency ?? localDigestFrequency;

  const handleDigestFrequencySelect = useCallback(
    async (nextFrequency) => {
      const isValid = EMAIL_FREQUENCY_OPTIONS.some((o) => o.value === nextFrequency);
      if (!isValid || savingFrequency || nextFrequency === digestFrequency) {
        return;
      }

      setSavingFrequency(true);
      setFrequencyStatus("");
      setFrequencyStatusMessage("");

      try {
        if (onEmailFrequencyChange) {
          // Parent (JobList) owns the state, persists it, and reverts on failure.
          const result = await onEmailFrequencyChange(nextFrequency);
          if (result && result.ok === false) {
            setFrequencyStatus("error");
            return;
          }
        } else {
          setLocalDigestFrequency(nextFrequency);
          await axios.post("/.netlify/functions/saveUserData", {
            userId,
            emailFrequency: nextFrequency,
            newsletterEnabled: nextFrequency !== "off",
          });
        }

        setFrequencyStatus("saved");
        setFrequencyStatusMessage(
          nextFrequency === "off"
            ? "Job digest emails turned off. You can re-enable them anytime."
            : `Saved — you'll receive a ${nextFrequency} digest.`
        );
      } catch (error) {
        console.error("Error saving email frequency:", error);
        setFrequencyStatus("error");
      } finally {
        setSavingFrequency(false);
      }
    },
    [
      digestFrequency,
      savingFrequency,
      userId,
      onEmailFrequencyChange,
    ]
  );

  if (!isOpen) return null;

  const displayEmail = userInfo?.email || userEmail || "N/A";
  const displayName =
    userInfo?.user_metadata?.full_name ||
    userInfo?.name ||
    userName ||
    "User";

  return (
    <div className="profile-panel-content">
      {userInfo ? (
        <div className="profile-details">
          <div className="profile-field">
            <label className="profile-field-label">Display name</label>
            {isEditingName ? (
              <div className="profile-field-edit-row">
                <input
                  type="text"
                  className="profile-field-input"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
                  autoFocus
                />
                <div className="profile-field-edit-actions">
                  <button
                    className="profile-save-button"
                    onClick={handleSaveName}
                    disabled={savingName}
                  >
                    {savingName ? "..." : "Save"}
                  </button>
                  <button
                    className="profile-cancel-button"
                    onClick={() => {
                      setIsEditingName(false);
                      setEditName(displayName);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="profile-field-value-row">
                <span className="profile-field-value">{displayName}</span>
                <button
                  className="profile-edit-button"
                  onClick={() => setIsEditingName(true)}
                >
                  Edit
                </button>
              </div>
            )}
          </div>

          <div className="profile-field">
            <label className="profile-field-label">Email</label>
            <span className="profile-field-value">{displayEmail}</span>
            <p className="profile-field-hint">
              Managed by your identity provider.
            </p>
          </div>

          <div className="profile-field">
            <label className="profile-field-label">Password</label>
            <button
              className="profile-action-button"
              onClick={handleChangePassword}
              disabled={sendingReset}
            >
              {sendingReset ? "Sending..." : "Send reset email"}
            </button>
            {passwordStatus === "success" && (
              <p className="profile-password-status profile-password-status--success">
                Check your inbox — a reset link has been sent.
              </p>
            )}
            {passwordStatus === "error" && (
              <p className="profile-password-status profile-password-status--error">
                Could not send reset email. Try again later.
              </p>
            )}
            {!passwordStatus && (
              <p className="profile-field-hint">
                We&apos;ll send a password reset link to your email.
              </p>
            )}
          </div>

          <div className="profile-field">
            <label className="profile-field-label" htmlFor="email-frequency-group">
              Job digest emails
            </label>
            <div
              className="profile-frequency-options"
              id="email-frequency-group"
              role="group"
              aria-label="Job digest email frequency"
            >
              {EMAIL_FREQUENCY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`profile-frequency-option${
                    digestFrequency === option.value ? " is-active" : ""
                  }`}
                  onClick={() => handleDigestFrequencySelect(option.value)}
                  disabled={savingFrequency}
                  aria-pressed={digestFrequency === option.value}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {frequencyStatus === "error" ? (
              <p className="profile-password-status profile-password-status--error">
                Could not save your preference. Please try again.
              </p>
            ) : frequencyStatusMessage ? (
              <p className="profile-password-status profile-password-status--success">
                {frequencyStatusMessage}
              </p>
            ) : (
              <p className="profile-field-hint">
                Choose how often we email your top job matches.
              </p>
            )}
          </div>

          {saveMessage && (
            <p className="profile-save-message">{saveMessage}</p>
          )}
        </div>
      ) : (
        <div className="loading">Loading...</div>
      )}
    </div>
  );
};

export default ProfileSidebar;